import { useCallback, useEffect, useRef, useState } from "react";
import { listClientSubmissions } from "@/features/efiling/api";
import type { IRSSubmission } from "@/features/efiling/types";
import { ApiError } from "@/lib/api";

interface Result {
  submissions: IRSSubmission[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetches a client's IRS submissions; race-guarded like use-client-esignatures. */
export function useClientSubmissions(clientId: string): Result {
  const [submissions, setSubmissions] = useState<IRSSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${clientId}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    listClientSubmissions(clientId)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setSubmissions(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setSubmissions([]);
        setError(err instanceof ApiError ? err.message : "Failed to load submissions.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId, reloadKey]);

  return { submissions, loading, error, refetch };
}
